import React, { useState, useEffect, useCallback } from 'react';
import './AdminPortal.css';

// Use environment variable or detect the current origin for API calls
const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production, use the same origin (relative URLs)
  // In development, use localhost
  if (process.env.NODE_ENV === 'production') {
    return ''; // Use relative URLs in production
  }
  return 'http://localhost:3001';
};

const API_BASE = getApiBase();

function AdminPortal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    location: '',
    status: '',
    email: '',
    limit: 50
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        skip: (currentPage - 1) * filters.limit
      });
      
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/api/submissions?${params}`, {
        headers
      });

      console.log('Submissions response status:', response.status);
      const responseData = await response.json();
      console.log('Submissions response:', responseData);

      if (response.status === 401) {
        console.error('Unauthorized - session not found');
        setIsAuthenticated(false);
        return;
      }

      if (responseData.success) {
        setSubmissions(responseData.submissions || []);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  }, [filters, currentPage]);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/api/stats`, {
        headers
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Load submissions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadSubmissions();
      loadStats();
    }
  }, [isAuthenticated, loadSubmissions, loadStats]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return false;
      }
      
      const response = await fetch(`${API_BASE}/api/admin/check-auth`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        setLoading(false);
        return false;
      }
      
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
      return data.authenticated;
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (data.success && data.token) {
        // Store token in localStorage
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
        setPassword('');
        // Load data immediately
        loadSubmissions();
        loadStats();
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (error) {
      setError('Failed to connect to server');
      console.error('Login error:', error);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setSubmissions([]);
    setStats(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-portal">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-portal">
        <div className="login-container">
          <div className="login-box">
            <h1>Level Up Insulation</h1>
            <h2>Admin Portal</h2>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  autoFocus
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="btn-login">Login</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-portal">
      <header className="admin-header">
        <div className="header-content">
          <h1>Level Up Insulation - Admin Portal</h1>
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </header>

      <div className="admin-content">
        {/* Stats Section */}
        {stats && (
          <div className="stats-section">
            <h2>Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Submissions</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.submitted}</div>
                <div className="stat-label">Submitted</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.drafts}</div>
                <div className="stat-label">Drafts</div>
              </div>
            </div>

            {stats.byLocation && stats.byLocation.length > 0 && (
              <div className="location-stats">
                <h3>By Location</h3>
                <div className="location-list">
                  {stats.byLocation.map((item, index) => (
                    <div key={index} className="location-item">
                      <span className="location-name">{item._id || 'Unknown'}</span>
                      <span className="location-count">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <h2>Filters</h2>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Location</label>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => {
                  setFilters({ ...filters, location: e.target.value });
                  setCurrentPage(1);
                }}
                placeholder="Filter by location"
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                <option value="submitted">Submitted</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Email</label>
              <input
                type="text"
                value={filters.email}
                onChange={(e) => {
                  setFilters({ ...filters, email: e.target.value });
                  setCurrentPage(1);
                }}
                placeholder="Filter by email"
              />
            </div>
            <div className="filter-group">
              <label>Limit</label>
              <select
                value={filters.limit}
                onChange={(e) => {
                  setFilters({ ...filters, limit: parseInt(e.target.value) });
                  setCurrentPage(1);
                }}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="submissions-section">
          <h2>Form Submissions ({submissions.length})</h2>
          {submissions.length === 0 ? (
            <div className="no-submissions">No submissions found</div>
          ) : (
            <div className="table-container">
              <table className="submissions-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Issue</th>
                    <th>Location</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission._id}>
                      <td>{formatDate(submission.createdAt)}</td>
                      <td>{submission.name || '-'}</td>
                      <td>{submission.email || '-'}</td>
                      <td>{submission.phone || '-'}</td>
                      <td>{submission.address || '-'}</td>
                      <td>{submission.issue || '-'}</td>
                      <td>{submission.location || '-'}</td>
                      <td>
                        <span className={`status-badge ${submission.status}`}>
                          {submission.status || 'draft'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPortal;

